import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ProjectType } from "../../project/project-type.enum";
import { AuditPackageGenerateService } from "./audit-query-quality/audit-package-generate/audit-package-generate.service";
import { AuditPackageGenerateRiskService } from "./audit-query-risk/audit-package-generate-risk/audit-package-generate-risk.service";
import { AuditQueryGridRiskService } from "./audit-query-risk/audit-query-grid-risk/audit-query-grid-risk.service";
import { AuditQueryGridFilters } from "./audit-query-shared/audit-query-grid-filters/audit-query-grid-filter-model";
import { AuditQueryGridFiltersService } from "./audit-query-shared/audit-query-grid-filters/audit-query-grid-filters.service";
import { AuditQueryHeaderService } from "./audit-query-shared/audit-query-header/audit-query-header.service";

enum AuditPackageType {
  RADV_AUDIT_PACKAGE = "RADV AUDIT PACKAGE",
  QUALITY_AUDIT_PACKAGE = "QUALITY AUDIT PACKAGE",
}

@Component({
  selector: "app-audit-query",
  templateUrl: "./audit-query.component.html",
  styleUrls: ["./audit-query.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQueryComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  selectedTab = "auditquery";
  stateName = "audit-query";
  showGrid = false;
  currentProjectType: number;
  currentProjectId: number;
  currentFilters: AuditQueryGridFilters;
  receivedMessage = "";
  private unsubscribe = new Subject();

  constructor(
    private auditQueryGridFiltersService: AuditQueryGridFiltersService,
    private auditQueryHeaderService: AuditQueryHeaderService,
    private auditQueryGridRiskService: AuditQueryGridRiskService,
    private auditPackageGenerateRiskService: AuditPackageGenerateRiskService,
    private auditPackageGenerateService: AuditPackageGenerateService,
    private readonly messagingService: MessagingService

  ) { }

  ngOnInit() {
    window.addEventListener("message", this.handleMessage.bind(this));

    this.sink.add(
      this.auditQueryGridFiltersService.auditGridFilter
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((filters: AuditQueryGridFilters) => {
          this.currentProjectType = filters.projectTypeId;
          this.currentProjectId = filters.projectId;
          this.currentFilters = filters;
          if (filters.projectTypeId === ProjectType.HEDIS) {
            this.showGrid = !!(filters.projectTypeId && filters.projectId);
          } else if (filters.projectTypeId === ProjectType.RISK) {
            this.showGrid = !!(filters.projectTypeId && filters.projectId && filters.enrollee);
          } else {
            this.showGrid = false;
          }
        })
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
    this.unsubscribe.next();
    this.unsubscribe.complete();
    window.removeEventListener("message", this.handleMessage.bind(this));
  }

  onSelectTab($event: string): void {
    this.selectedTab = $event;
  }

  get isQualityProjectType(): boolean {
    return this.currentProjectType === ProjectType.HEDIS;
  }

  get isRiskProjectType(): boolean {
    return this.currentProjectType === ProjectType.RISK;
  }

  private handleMessage(event: MessageEvent) {
    if (event.origin.includes("reveleer.com") && event.data?.type) {
      this.receivedMessage = event.data;
      const auditPackageType = event.data.type;

      if ((this.receivedMessage as any)?.data?.success) {
        // SetTimeout is used here to allow the previous dom to get destroyed before loading the new dom for audit query tab.
        setTimeout(() => {
          if (auditPackageType === AuditPackageType.RADV_AUDIT_PACKAGE) {
            this.refreshAuditQueryRiskGrid();
          } else if (auditPackageType === AuditPackageType.QUALITY_AUDIT_PACKAGE) {
            this.refreshAuditQueryGrid();
          }
        },         500);
        this.messagingService.showToast("Page selection was successful.", SeverityType.SUCCESS);
      } else {
        this.messagingService.showToast("Failed to save page selection.", SeverityType.ERROR);
      }
      this.auditQueryHeaderService.changeTab("auditquery");
    }
  }

  private refreshAuditQueryRiskGrid(): void {
    if (this.currentFilters?.enrollee) {
      this.auditQueryGridFiltersService.getMemberRadvCard(this.currentFilters.enrollee)
        .subscribe({
          next: radvCardData => {
            this.auditQueryGridRiskService.sendMemberRadvCardData(radvCardData);
            this.auditQueryGridRiskService.reloadAuditGridFromState();
            this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.currentProjectId);
          },
          error: () => {
            this.auditQueryGridRiskService.reloadAuditGridFromState();
            this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.currentProjectId);
          },
        });
    } else {
      this.auditQueryGridRiskService.reloadAuditGridFromState();
      this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.currentProjectId);
    }
  }

  private refreshAuditQueryGrid(): void {
    this.auditPackageGenerateService.refreshAuditPackageGrid(this.currentProjectId);
  }

}
